if (!customElements.get('bcm-video-hero')) {
  customElements.define('bcm-video-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.video = this.querySelector('[data-bcm-hero-video]');
      this.toggle = this.querySelector('[data-bcm-video-toggle]');
      if (!this.video || !this.toggle) return;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.selection = null;
      this.generation ??= 0;
      this.resumeWhenVisible = false;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.addEventListener('playing', () => {
        if (document.hidden || this.motionQuery.matches || this.userPaused || !this.selection) {
          this.resumeWhenVisible = document.hidden && !this.userPaused;
          this.video.pause();
          return;
        }
        window.clearTimeout(this.loadTimer);
        this.video.setAttribute('data-ready', '');
        this.toggle.hidden = false;
        this.updateLabel();
      }, options);
      this.video.addEventListener('pause', () => this.updateLabel(), options);
      this.video.addEventListener('error', () => {
        if (this.selection && this.video.error) this.resetVideo();
      }, options);
      this.toggle.addEventListener('click', () => {
        if (this.video.paused) {
          this.userPaused = false;
          this.cancelScheduledVideo();
          if (!this.selection) this.selectVideo();
          else this.startPlayback();
        } else {
          this.userPaused = true;
          this.resumeWhenVisible = false;
          this.video.pause();
        }
      }, options);
      this.mobileQuery.addEventListener('change', () => this.scheduleVideo(), options);
      this.motionQuery.addEventListener('change', () => this.scheduleVideo(), options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.resumeWhenVisible = !this.video.paused;
          this.video.pause();
        } else if (this.resumeWhenVisible && !this.userPaused && !this.motionQuery.matches) {
          this.resumeWhenVisible = false;
          this.startPlayback();
        }
      }, options);
      this.scheduleVideo();
    }

    disconnectedCallback() {
      if (!this.abortController) return;
      this.cancelScheduledVideo();
      this.abortController.abort();
      this.abortController = null;
      this.resetVideo();
    }


    cancelScheduledVideo() {
      this.scheduleGeneration = (this.scheduleGeneration || 0) + 1;
      window.clearTimeout(this.startTimer);
      if (this.idleHandle != null && window.cancelIdleCallback) {
        window.cancelIdleCallback(this.idleHandle);
      }
      this.idleHandle = null;
    }

    scheduleVideo() {
      this.cancelScheduledVideo();
      this.resetVideo();
      if (this.motionQuery.matches) return;
      this.toggle.hidden = false;
      this.updateLabel();
      if (navigator.connection?.saveData) return;
      const generation = this.scheduleGeneration;
      const poster = this.querySelector('picture img');
      let scheduled = false;
      const prepare = () => {
        if (scheduled || generation !== this.scheduleGeneration || !this.isConnected) return;
        if (document.readyState !== 'complete' || (poster && !poster.complete)) return;
        scheduled = true;
        this.startTimer = window.setTimeout(() => {
          const start = () => {
            if (generation === this.scheduleGeneration && this.isConnected) this.selectVideo();
          };
          if (window.requestIdleCallback) {
            this.idleHandle = window.requestIdleCallback(start, { timeout: 2000 });
          } else {
            start();
          }
        }, 1000);
      };
      const options = { once: true, signal: this.abortController.signal };
      window.addEventListener('load', prepare, options);
      poster?.addEventListener('load', prepare, options);
      poster?.addEventListener('error', prepare, options);
      prepare();
    }

    updateLabel() {
      this.toggle.setAttribute('aria-label', this.video.paused
        ? this.toggle.dataset.playLabel
        : this.toggle.dataset.pauseLabel);
      this.toggle.dataset.state = this.video.paused ? 'paused' : 'playing';
    }

    resetVideo() {
      this.generation += 1;
      window.clearTimeout(this.loadTimer);
      this.video.pause();
      this.video.removeAttribute('data-ready');
      this.video.removeAttribute('src');
      this.video.load();
      this.toggle.hidden = true;
      this.selection = null;
      this.resumeWhenVisible = false;
    }

    selectVideo() {
      const viewport = this.mobileQuery.matches ? 'mobile' : 'desktop';
      if (this.motionQuery.matches) {
        this.resetVideo();
        return;
      }
      if (this.selection === viewport) return;
      this.resetVideo();
      const template = this.querySelector(`[data-bcm-video-sources="${viewport}"]`);
      const candidates = [...(template?.content.querySelectorAll('source') || [])]
        .filter(source => source.getAttribute('src') && this.video.canPlayType(source.type));
      const mp4s = candidates.filter(source => source.dataset.format === 'mp4');
      const sources = mp4s.length ? mp4s : candidates;
      sources.sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
      const targetWidth = Math.min(this.clientWidth * Math.min(window.devicePixelRatio || 1, 1.5), this.mobileQuery.matches ? 1080 : 1920);
      const source = sources.find(candidate => Number(candidate.dataset.width) >= targetWidth) || sources.at(-1);
      if (!source) return;
      this.selection = viewport;
      this.video.src = source.getAttribute('src');
      this.video.muted = true;
      this.toggle.hidden = false;
      this.updateLabel();
      if (!this.userPaused) {
        if (document.hidden) this.resumeWhenVisible = true;
        else this.startPlayback();
      }
    }

    startPlayback() {
      if (!this.selection || this.motionQuery.matches || this.userPaused) return;
      const generation = ++this.generation;
      window.clearTimeout(this.loadTimer);
      this.loadTimer = window.setTimeout(() => {
        if (generation === this.generation && !this.video.hasAttribute('data-ready')) this.resetVideo();
      }, 12000);
      const playback = this.video.play();
      if (playback && typeof playback.catch === 'function') {
        playback.catch(() => {
          if (generation !== this.generation) return;
          window.clearTimeout(this.loadTimer);
          this.video.removeAttribute('data-ready');
          this.video.pause();
          this.updateLabel();
        });
      }
    }
  });
}

